import { useMemo, useState } from "react";
import type { NormalizedSplitItem, OrderItem, SplitItem, SplitType } from "./types";
import { getSplitItemKey } from "./splitItemIdentity.ts";

export const reconcileSplitItems = ({
  availableItems,
  hasSplit,
  splitType,
  isSameActiveOrder,
  previousSplitItems,
}: {
  availableItems: OrderItem[];
  hasSplit: boolean;
  splitType: SplitType | null;
  isSameActiveOrder: boolean;
  previousSplitItems: SplitItem[];
}): SplitItem[] => {
  if (hasSplit && splitType === "items") {
    return availableItems.map((item) => ({ ...item, split_quantity: item.quantity }));
  }

  if (!isSameActiveOrder) {
    return [];
  }

  return previousSplitItems
    .map((prevItem): SplitItem | null => {
      const nextItem = availableItems.find(
        (item) => getSplitItemKey(item) === getSplitItemKey(prevItem)
      );
      if (!nextItem) return null;

      return {
        ...nextItem,
        split_quantity: Math.min(
          Number(prevItem.split_quantity || 1),
          Number(nextItem.quantity || 1)
        ),
      };
    })
    .filter((item): item is SplitItem => item !== null);
};

export const useSplitItems = ({
  availableItems,
}: {
  availableItems: OrderItem[];
}) => {
  const [splitItems, setSplitItems] = useState<SplitItem[]>([]);

  const normalizedSplitItems = useMemo<NormalizedSplitItem[]>(
    () => splitItems.map((item) => ({ ...item, quantity: item.split_quantity })),
    [splitItems]
  );

  const handleSplitItemToggle = (item: OrderItem): void => {
    const itemKey = getSplitItemKey(item);
    const exists = splitItems.find((splitItem) => getSplitItemKey(splitItem) === itemKey);

    if (exists) {
      setSplitItems(splitItems.filter((splitItem) => getSplitItemKey(splitItem) !== itemKey));
      return;
    }

    setSplitItems([...splitItems, { ...item, split_quantity: 1 }]);
  };

  const handleSplitQuantityChange = (item: OrderItem, change: number): void => {
    const itemKey = getSplitItemKey(item);
    setSplitItems(
      splitItems.map((splitItem) => {
        if (getSplitItemKey(splitItem) === itemKey) {
          const newQty = Math.max(1, Math.min(item.quantity, splitItem.split_quantity + change));
          return { ...splitItem, split_quantity: newQty };
        }
        return splitItem;
      })
    );
  };

  const handleSelectAllSplitItems = (): void => {
    setSplitItems(
      availableItems.map((item) => ({
        ...item,
        split_quantity: Number(item.quantity || 1),
      }))
    );
  };

  const handleDeselectAllSplitItems = (): void => {
    setSplitItems([]);
  };

  return {
    splitItems,
    setSplitItems,
    normalizedSplitItems,
    handleSplitItemToggle,
    handleSplitQuantityChange,
    handleSelectAllSplitItems,
    handleDeselectAllSplitItems,
  };
};
